import { BaseModal } from "@/shared-components/BaseModal";
import { mixpanel } from "@/utils/mixpanel";
import React, { useEffect, useState } from "react";

interface RequireTypedConfirmation {
  word: string;
  placeholder: string;
  label: string;
}

interface ConfirmProps {
  show: boolean;
  title: string;
  body: string | React.ReactElement;
  onYes: () => void;
  onNo: () => void;
  yesLabel: string;
  noLabel: string;
  yesTestId?: string;
  modalTestId?: string;
  // Set only for delete usages; drives Mixpanel tracking below.
  entityName?: string;
  destructive?: boolean;
  closeOnEscape?: boolean;
  requireTypedConfirmation?: RequireTypedConfirmation;
}

const trackDeleteModal = (entityName: string | undefined, action: string) => {
  if (!entityName) {
    return;
  }
  mixpanel.track("Delete Confirmation Modal", {
    Action: action,
    Entity: entityName,
  });
};

export const ConfirmationModal = ({
  show,
  title,
  body,
  onYes,
  onNo,
  yesLabel = "",
  noLabel = "",
  yesTestId,
  modalTestId,
  entityName,
  destructive = false,
  closeOnEscape = false,
  requireTypedConfirmation,
}: ConfirmProps) => {
  const [typedValue, setTypedValue] = useState("");

  useEffect(() => {
    if (show) {
      setTypedValue("");
      trackDeleteModal(entityName, "Delete Modal Opened");
    }
  }, [show]);

  const handleYes = () => {
    trackDeleteModal(entityName, "Delete Modal Confirmed");
    onYes();
  };

  const handleCancel = () => {
    trackDeleteModal(entityName, "Delete Modal Cancelled");
    onNo();
  };

  const handleDismiss = () => {
    trackDeleteModal(entityName, "Delete Modal Dismissed");
    onNo();
  };

  const typedConfirmed =
    !requireTypedConfirmation ||
    typedValue.trim().toLowerCase() ===
      requireTypedConfirmation.word.toLowerCase();

  const resolvedBody = requireTypedConfirmation ? (
    <>
      {typeof body === "string" ? <p>{body}</p> : body}
      <label className="mt-2 mb-1 d-block">
        {requireTypedConfirmation.label}
      </label>
      <input
        type="text"
        value={typedValue}
        placeholder={requireTypedConfirmation.placeholder}
        onChange={(e) => setTypedValue(e.target.value)}
        className="form-control"
      />
    </>
  ) : (
    body
  );

  return (
    <BaseModal
      show={show}
      title={title}
      body={resolvedBody}
      onYes={handleYes}
      onNo={handleCancel}
      onDismiss={handleDismiss}
      yesLabel={yesLabel}
      noLabel={noLabel}
      yesTestId={yesTestId}
      modalTestId={modalTestId}
      yesDisabled={!typedConfirmed}
      yesVariant={destructive ? "danger" : "primary"}
      closeOnEscape={closeOnEscape}
      mouseOnlyConfirm={destructive}
    />
  );
};
